// AdminDiscordPage — Discord server integration status, role mapping, channel sync
// Per marketing: Discord required for marketing attribution

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface DiscordMember {
  id: string;
  username: string;
  avatar: number;
  funderAccount: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  discordRole: 'Beginner' | 'Mid' | 'Pro' | 'VIP' | 'Support' | 'Staff';
  status: 'linked' | 'unlinked' | 'expired';
  joined: string;
  lastSeen: string;
}

const MEMBERS: DiscordMember[] = [
  { id: 'm1', username: 'marcus.j#1284', avatar: 1, funderAccount: 'FP-100K-14283', tier: 'Mid',          discordRole: 'Mid',     status: 'linked',   joined: '2026-04-12', lastSeen: '2m ago' },
  { id: 'm2', username: 'sarah.c#9182',  avatar: 2, funderAccount: 'FP-150K-12844', tier: 'Professional', discordRole: 'Pro',     status: 'linked',   joined: '2026-05-01', lastSeen: '12m ago' },
  { id: 'm3', username: 'riley.t#2241',  avatar: 3, funderAccount: 'FP-50K-15201',  tier: 'Beginner',     discordRole: 'Beginner',status: 'linked',   joined: '2026-07-04', lastSeen: '1h ago' },
  { id: 'm4', username: 'devon.k#0812',  avatar: 4, funderAccount: 'FP-100K-13892', tier: 'Mid',          discordRole: 'Mid',     status: 'expired',  joined: '2026-06-12', lastSeen: '3d ago' },
  { id: 'm5', username: 'lisa.h#9921',   avatar: 4, funderAccount: '—',             tier: 'Beginner',     discordRole: 'Beginner',status: 'unlinked', joined: '2026-03-15', lastSeen: '1w ago' },
  { id: 'm6', username: 'brent.o#0042',  avatar: 3, funderAccount: 'FP-100K-14821', tier: 'Mid',          discordRole: 'VIP',     status: 'linked',   joined: '2026-07-10', lastSeen: '5m ago' },
];

interface Channel {
  id: string;
  name: string;
  topic: string;
  members: number;
  syncStatus: 'active' | 'paused' | 'error';
  lastSync: string;
}

const CHANNELS: Channel[] = [
  { id: 'c1', name: 'announcements', topic: 'New challenges, system updates',    members: 1820, syncStatus: 'active', lastSync: '2m ago' },
  { id: 'c2', name: 'general-trading', topic: 'Trader chat',                       members: 1620, syncStatus: 'active', lastSync: '12s ago' },
  { id: 'c3', name: 'beginner-tier',  topic: 'Beginner ($50K) discussion',         members: 480,  syncStatus: 'active', lastSync: '30s ago' },
  { id: 'c4', name: 'mid-tier',       topic: 'Mid ($100K) discussion',              members: 720,  syncStatus: 'active', lastSync: '8s ago' },
  { id: 'c5', name: 'pro-tier',       topic: 'Professional ($150K) discussion',     members: 320,  syncStatus: 'paused', lastSync: '1h ago' },
  { id: 'c6', name: 'payouts',        topic: 'Payout confirmations',                members: 1240, syncStatus: 'active', lastSync: '4m ago' },
  { id: 'c7', name: 'bot-strategies', topic: 'AI bot marketplace + discussion',     members: 580,  syncStatus: 'error',  lastSync: '2h ago' },
];

const TIER_COLOR = { Beginner: '#34C759', Mid: '#0A84FF', Professional: '#1C1C1E' };
const ROLE_COLOR: Record<string, string> = { Beginner: '#34C759', Mid: '#0A84FF', Pro: '#1C1C1E', VIP: '#FFD60A', Support: '#FF9500', Staff: '#FF3B30' };

export function AdminDiscordPage() {
  const nav = useNavigate();
  const [members] = useState(MEMBERS);
  const [channels] = useState(CHANNELS);
  const [tab, setTab] = useState<'members' | 'channels' | 'roles'>('members');

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Discord</div>
        <button onClick={() => alert('Settings opened')} data-testid="discord-settings" className="px-3 text-[15px] text-[#0A84FF]">Settings</button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-3 rounded-[16px] border border-white/10 p-4" style={{ background: 'linear-gradient(135deg, #5865F2 0%, #404EED 100%)' }}>
        <div className="flex items-center gap-3">
          <div className="text-[36px]">💬</div>
          <div className="flex-1">
            <div className="text-[18px] font-bold">Funder Pro Discord</div>
            <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.85)' }}>discord.gg/funderpro · 4,820 members</div>
          </div>
          <div className="rounded-full bg-[#34C759] px-2.5 py-1 text-[10px] font-bold uppercase text-black">Connected</div>
        </div>
      </div>

      {/* Stats */}
      <div className="mx-4 mt-3 grid grid-cols-4 gap-2">
        <Stat label="Members" value={members.length} color="#5865F2" />
        <Stat label="Linked" value={members.filter((m) => m.status === 'linked').length} color="#34C759" />
        <Stat label="Expired" value={members.filter((m) => m.status === 'expired').length} color="#FF9500" />
        <Stat label="Channels" value={channels.length} color="#0A84FF" />
      </div>

      {/* Tabs */}
      <div className="px-4 pt-3 flex items-center gap-1.5">
        {(['members', 'channels', 'roles'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              tab === t ? 'bg-white text-black' : 'bg-white/10 text-white/70 active:opacity-40'
            ].join(' ')}
          >{t}</button>
        ))}
      </div>

      {/* Members tab */}
      {tab === 'members' && (
        <div className="px-4 mt-3 space-y-1.5">
          {members.map((m) => (
            <div key={m.id} data-testid={`member-${m.id}`} className="rounded-[12px] border border-white/10 bg-white/5 p-2.5 flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold bg-[#5865F2]">{m.username[0].toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] font-bold text-white truncate">{m.username}</span>
                  <span className="rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase" style={{ backgroundColor: ROLE_COLOR[m.discordRole] + '30', color: ROLE_COLOR[m.discordRole] }}>{m.discordRole}</span>
                </div>
                <div className="text-[10px] text-white/50 font-mono">{m.funderAccount} · last seen {m.lastSeen}</div>
              </div>
              <div className={['rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase',
                m.status === 'linked' ? 'bg-[#34C759]/20 text-[#34C759]' :
                m.status === 'expired' ? 'bg-[#FF9500]/20 text-[#FF9500]' :
                'bg-white/10 text-white/50'
              ].join(' ')}>{m.status}</div>
            </div>
          ))}
        </div>
      )}

      {/* Channels tab */}
      {tab === 'channels' && (
        <div className="px-4 mt-3 space-y-1.5">
          {channels.map((c) => {
            const sColor = c.syncStatus === 'active' ? '#34C759' : c.syncStatus === 'paused' ? '#FF9500' : '#FF3B30';
            return (
              <div key={c.id} data-testid={`channel-${c.id}`} className="rounded-[12px] border border-white/10 bg-white/5 p-2.5 flex items-center gap-2.5">
                <div className="text-[18px]">#</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-bold text-white">#{c.name}</div>
                  <div className="text-[10px] text-white/50">{c.topic} · {c.members} members</div>
                </div>
                <div className="text-right">
                  <div className="rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase" style={{ backgroundColor: sColor + '20', color: sColor }}>{c.syncStatus}</div>
                  <div className="text-[9px] text-white/40 mt-0.5">last {c.lastSync}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Roles tab */}
      {tab === 'roles' && (
        <div className="px-4 mt-3 space-y-2">
          {(['Beginner', 'Mid', 'Pro', 'VIP', 'Support', 'Staff'] as const).map((r) => (
            <div key={r} data-testid={`role-${r}`} className="rounded-[12px] border border-white/10 bg-white/5 p-3 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-[8px] text-[14px] font-bold" style={{ backgroundColor: ROLE_COLOR[r] + '30', color: ROLE_COLOR[r] }}>{r[0]}</div>
              <div className="flex-1">
                <div className="text-[13px] font-bold text-white">{r}</div>
                <div className="text-[10px] text-white/50">{members.filter((m) => m.discordRole === r).length} members</div>
              </div>
              <button onClick={() => alert(`Edited role ${r}`)} data-testid={`edit-role-${r}`} className="text-[12px] text-[#0A84FF] active:opacity-40">Edit</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
      <div className="text-[9px] text-white/60 uppercase">{label}</div>
      <div className="text-[16px] font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
